import type React from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineAttachment, MdClose, MdImage } from "react-icons/md";
import {
  getScenarioHelperContextPdfs,
  addScenarioHelperContextPdf,
  removeScenarioHelperContextPdf,
  getScenarioHelperContextImages,
  addScenarioHelperContextImage,
  removeScenarioHelperContextImage,
} from "../state/chatbotSlice";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IMedia } from "src/model/unityObject";
import { useMediaPicker } from "src/features/gallery/picker/useMediaPicker";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { FloatingTooltip } from "../../../common/components/Tooltip/FloatingTooltip";
import { ATTACHMENT_LIMITS } from "../utils/skipThreshold";
import { TruncatedText } from "../../../common/components/TruncatedText/TruncatedText";

export const isMediaProcessing = (m: IMedia): boolean =>
  !!m.upload_status && m.upload_status !== "completed";

const PROCESSING_LABEL_KEYS: Record<string, string> = {
  initialized: "pages.chat.attachProcessingInitialized",
  pending: "pages.chat.attachProcessingPending",
  transcoding: "pages.chat.attachProcessingTranscoding",
  transcribing: "pages.chat.attachProcessingTranscribing",
};

const ProcessingBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600">
      <span className="inline-block w-2.5 h-2.5 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" />
      {t(PROCESSING_LABEL_KEYS[status] || "pages.chat.attachProcessingDefault")}
    </span>
  );
};

export interface AttachContextMediaProps {
  onMediaAdded?: (media: IMedia, track: "pdf" | "image") => void;
  isEditionEnabled: boolean;
  /** Plan-stage delegation: when set, the image button no longer opens this
   * component's own gallery — the click is handed to the caller wholesale
   * (which routes it through the shared reference-image picker), and the
   * button stays usable even with edition disabled. PDFs are untouched. */
  onImageButtonOverride?: () => void;
  /** True while a reference-image change request is in flight — keeps the
   * image button from opening a picker mid-request. Ignored on the PDF
   * button, which already follows isEditionEnabled. */
  isImagePickBusy?: boolean;
  /** True while a decor-realign REMOVAL is in flight: the decor chip still
   * mirrors the not-yet-updated server context, so showing it would claim
   * "décor 360°" for an image being removed. Hidden until the turn lands. */
  hideDecorImages?: boolean;
}

const AttachContextMedia: React.FC<AttachContextMediaProps> = ({
  onMediaAdded,
  isEditionEnabled = true,
  onImageButtonOverride,
  isImagePickBusy = false,
  hideDecorImages = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedPdfs = useAppSelector(getScenarioHelperContextPdfs);
  const allSelectedImages = useAppSelector(getScenarioHelperContextImages);
  const selectedImages = hideDecorImages ? [] : allSelectedImages;
  const { pickMedia, pickerElement } = useMediaPicker();

  const openPicker = async (track: "pdf" | "image") => {
    if (track === "pdf" && selectedPdfs.length >= ATTACHMENT_LIMITS.PDF_MAX) {
      dispatch(
        setAlert({ type: "warning", msg: t("pages.chat.attachMaxPdf"), customTimeoutInMs: 3000 }),
      );
      return;
    }
    if (track === "image" && selectedImages.length >= ATTACHMENT_LIMITS.IMAGE_MAX) {
      dispatch(
        setAlert({ type: "warning", msg: t("pages.chat.attachMaxImage"), customTimeoutInMs: 3000 }),
      );
      return;
    }
    const newMedia = await pickMedia({
      allowedMediaTypes: track,
      initialSearch: { mediaType: track, media360tag: "", query: "", page: 0 },
    });
    if (!newMedia) return;
    if (track === "image") {
      dispatch(addScenarioHelperContextImage(newMedia));
      if (onMediaAdded) onMediaAdded(newMedia, "image");
    } else {
      dispatch(addScenarioHelperContextPdf(newMedia));
      if (onMediaAdded) onMediaAdded(newMedia, "pdf");
    }
  };

  const hasAttachments = selectedPdfs.length > 0 || selectedImages.length > 0;

  return (
    <div
      className="flex items-center gap-2 flex-wrap mb-1 max-h-20 overflow-y-auto"
      data-tour="scenaria-workbench-attachments"
    >
      <FloatingTooltip content={t("pages.chat.attachPdfContextualTooltip")} wrap ariaHidden>
        <button
          type="button"
          aria-label={t("pages.chat.attachPdfContextualTooltip")}
          onClick={() => void openPicker("pdf")}
          disabled={!isEditionEnabled}
          className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 transition-colors"
        >
          <MdOutlineAttachment size={20} />
        </button>
      </FloatingTooltip>
      <FloatingTooltip content={t("pages.chat.attachImageContextualTooltip")} wrap ariaHidden>
        <button
          type="button"
          aria-label={t("pages.chat.attachImageContextualTooltip")}
          onClick={() =>
            onImageButtonOverride ? onImageButtonOverride() : void openPicker("image")
          }
          disabled={(!isEditionEnabled && !onImageButtonOverride) || isImagePickBusy}
          className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 transition-colors"
        >
          <MdImage size={20} />
        </button>
      </FloatingTooltip>

      {hasAttachments && (
        <>
          <div className="w-px h-5 bg-gray-200" />
          {selectedPdfs.map((pdf) => (
            <div
              key={pdf.id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-xs ${
                isMediaProcessing(pdf)
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-gray-100 border-gray-200 text-gray-600"
              }`}
            >
              <MdOutlineAttachment size={12} />
              <TruncatedText className="max-w-[120px]" text={pdf.filename || pdf.name} />
              {isMediaProcessing(pdf) ? (
                <ProcessingBadge status={pdf.upload_status} />
              ) : isEditionEnabled ? (
                <button
                  onClick={() => dispatch(removeScenarioHelperContextPdf(pdf.id))}
                  className="text-gray-400 hover:text-red-500 ml-0.5"
                >
                  <MdClose size={12} />
                </button>
              ) : null}
            </div>
          ))}
          {selectedImages.map((img) => (
            <FloatingTooltip key={img.id} content={t("pages.chat.attachImageDecorTooltip")}>
              <div
                tabIndex={0}
                className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-xs ${
                  isMediaProcessing(img)
                    ? "bg-amber-50 border-amber-200 text-amber-700"
                    : "bg-purple-50 border-purple-200 text-purple-700"
                }`}
              >
                <img
                  src={img.thumbnail_s3_url || img.s3_url}
                  alt={img.filename || img.name}
                  className="h-4 w-4 rounded object-cover shrink-0"
                />
                <TruncatedText className="max-w-[120px]" text={img.filename || img.name} />
                <span className="shrink-0 opacity-70">
                  · {t("pages.chat.attachImageDecorMarker")}
                </span>
                {isMediaProcessing(img) ? (
                  <ProcessingBadge status={img.upload_status} />
                ) : isEditionEnabled ? (
                  <button
                    onClick={() => dispatch(removeScenarioHelperContextImage(img.id))}
                    className="text-gray-400 hover:text-red-500 ml-0.5"
                  >
                    <MdClose size={12} />
                  </button>
                ) : null}
              </div>
            </FloatingTooltip>
          ))}
        </>
      )}

      {pickerElement}
    </div>
  );
};

export default AttachContextMedia;
